import React, { useMemo, useState } from 'react';
import { hashCode } from '@lib/string';
import styles from './ReadMoreTextBox.module.scss';
import ChevronDown from './chevron-down.svg';

// RegExp objects are stateful, so create a new one each time.
const getUrlPattern = () => (/\b(https?:\/\/\S*\b)/g);

/**
 * Finds any URLs within the content and replaces them with anchor <a> tags
 *
 * exported for testing
 */
const formatMessageWithHyperlinks = (content: string) => {
  const urlPattern = getUrlPattern();
  let idx = 0;
  let match;

  const parts = [];

  // eslint-disable-next-line no-cond-assign
  while ((match = urlPattern.exec(content)) != null) {
    const text = content.substring(idx, match.index);
    if (text.length > 0) {
      parts.push(text);
    }
    const url = match[0];
    parts.push(
      <a
        key={hashCode(url)}
        target="_blank"
        rel="noreferrer"
        href={url}
      >
        {url}
      </a>,
    );
    idx = urlPattern.lastIndex;
  }

  if (idx < content.length) {
    const text = content.substring(idx);
    if (text.length > 0) {
      parts.push(text);
    }
  }

  return parts;
};

interface IReadMoreTextBoxProps {
  maxCharThreshold: number;
  textContent: string;
  className?: string;
}

const ReadMoreTextBox = ({
  maxCharThreshold,
  textContent,
  className = styles.TextContent
}: IReadMoreTextBoxProps) => {
  const [readMoreOpen, setReadMoreOpen] = useState(false);

  const renderContent = (
    content: string, readMoreTreatment: boolean, readMoreButton:React.ReactElement | null = null
  ) => {
    let text = content;
    if (readMoreTreatment) {
      text = `${text.substr(0, maxCharThreshold)}...`;
    }

    const paragraphCount = text.split(/[\n\r]+/).length;

    return text.split(/[\n\r]+/)
      .map((part, index) => (
        <p key={hashCode(part)}>
          {formatMessageWithHyperlinks(part)}{paragraphCount === index + 1 ? readMoreButton : null}
        </p>
      ));
  };

  const shouldShowReadMore = useMemo(() => textContent &&
  textContent.length > maxCharThreshold, [textContent, maxCharThreshold]);

  const readMoreButton = (
    <button
      className={styles.ReadMoreButton}
      onClick={() => setReadMoreOpen(!readMoreOpen)}
      type="button"
    >
      <strong>
        {readMoreOpen ? `Read Less` : 'Read More'}{' '}
        {readMoreOpen ? <ChevronDown className={styles.FlipIt} /> : <ChevronDown />}
      </strong>
    </button>
  );

  return (
    <div className={`${className} ${readMoreOpen ? ` ${styles.ReadMore}` : ''}`}>
      {textContent && renderContent(
        textContent,
        !!shouldShowReadMore && !readMoreOpen,
        shouldShowReadMore ? readMoreButton : null
      )}
    </div>
  );
};

export default ReadMoreTextBox;
